import { toDayMs } from './shipmentSchedule.js';

/**
 * Arrivals grouped into the calendar days they land on — the shape the map's
 * Arrivals pop-up lists.
 *
 * Pure, and weight is injected rather than imported: what counts as a row's
 * weight depends on the selected commodity and display unit, both of which
 * live in the page. Keeping that out means this can be tested without the
 * shipment payload's full shape.
 *
 * Days with nothing arriving are not invented here. The design shows a "0
 * boxes" day (Sep 25), but an empty day is only knowable from the window's
 * bounds, not from its rows — `padDays` below is what fills those in.
 *
 * @param {object[]} rows      `windowRows()` output: one per arriving unit.
 * @param {Function} weightOf  row -> number in the display unit.
 * @returns {Array<{dayMs: number, total: number, rows: object[]}>} ascending by day.
 */
export const arrivalsByDay = (rows = [], weightOf = () => 0) => {
  const byDay = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const dayMs = toDayMs(row?.estimated_arrival);
    if (dayMs == null) continue;              // no ETA: nothing to file it under
    let day = byDay.get(dayMs);
    if (!day) {
      day = { dayMs, total: 0, rows: [] };
      byDay.set(dayMs, day);
    }
    day.rows.push(row);
    day.total += Number(weightOf(row)) || 0;
  }
  return [...byDay.values()].sort((a, b) => a.dayMs - b.dayMs);
};

/**
 * Fills in the days the window covers that nothing arrives on.
 *
 * The list reads as a calendar, so a silent gap between Sep 24 and Sep 26
 * invites the question of whether Sep 25 is empty or missing. An explicit
 * zero row answers it.
 *
 * @param {Array} days   `arrivalsByDay()` output.
 * @param {number} span  Window length in days (3, 7 or 10).
 * @param {Date}   now   Today.
 */
export const padDays = (days = [], span = 7, now = new Date()) => {
  const start = toDayMs(now);
  if (start == null || !Number.isFinite(span)) return days;
  const have = new Map(days.map((d) => [d.dayMs, d]));
  const out = [];
  const cursor = new Date(start);
  for (let i = 0; i <= span; i += 1) {
    const ms = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + i).getTime();
    out.push(have.get(ms) || { dayMs: ms, total: 0, rows: [] });
  }
  return out;
};

export default arrivalsByDay;
